import {
  CHECKOUT_VERSION,
  ECountry,
  EEntryType,
  EFlowType,
  EFunnelSource,
  EPaymentMethod,
  EReviewTarget,
  EStorageKey,
} from 'common/const';
import { getOrganizationCountry } from 'common/helpers/country.helper';
import { IOrganization } from 'common/types/organization.types';
import { ICheckoutSnapshot, IFunnelParams, ILandingParams, IReviewParams, TFunnelCurrency } from 'common/types/metrika.types';

/* Воронка Яндекс Метрики (metrika-funnel, metrika-landing; docs/system/payment-page-analytics.md, ADR-018).
 *  Хранилище устройства: снимок начатого платежа и отметки отправленных завершений. Любой сбой
 *  хранилища (недоступно, квота, битый JSON) - как будто данных нет: оплате не мешает, Sentry молчит (R-7). */

/* Снимок действует 60 минут от начала попытки (metrika-landing, R-2, R-4). */
export const CHECKOUT_SNAPSHOT_TTL_MS = 60 * 60 * 1000;
/* Отметки отправленных завершений хранятся 30 дней (R-4). */
export const COMPLETED_MARK_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const KNOWN_SOURCES: string[] = [EFunnelSource.NfcCube, EFunnelSource.QrCode];
const PAYMENT_METHODS: string[] = Object.values(EPaymentMethod);
const FLOW_TYPES: string[] = Object.values(EFlowType);

interface ICompletedMark {
  tuid: string;
  sent_at: number;
}

/* Значение метки или параметра адреса: nfc_cube и qr_code как есть, иное - unknown (metrika-landing, R-1). */
export const parseFunnelSource = (raw: string | null): EFunnelSource =>
  raw !== null && KNOWN_SOURCES.includes(raw) ? (raw as EFunnelSource) : EFunnelSource.Unknown;

export const getFlowType = (organization: IOrganization): EFlowType =>
  organization.review_subscription ? EFlowType.TipsReview : EFlowType.TipsOnly;

/* Валюта по стране заведения; без страны - как RS (F-12). */
export const getFunnelCurrency = (organization?: IOrganization | null): TFunnelCurrency =>
  getOrganizationCountry(organization) === ECountry.ME ? 'EUR' : 'RSD';

export const getFunnelParams = (organization: IOrganization, source: EFunnelSource): IFunnelParams => ({
  org_id: organization.id,
  source,
  flow_type: getFlowType(organization),
  checkout_version: CHECKOUT_VERSION,
});

/* Параметры экрана оценки; без полученного заведения - undefined, цели уходят без параметров (R-8). */
export const getReviewParams = (
  organization: IOrganization | null | undefined,
  source: EFunnelSource,
  target: EReviewTarget,
): IReviewParams | undefined => (organization ? { ...getFunnelParams(organization, source), review_target: target } : undefined);

/* Номер платежа из ответа бэка: целое положительное число, иначе null - ответ старого бэка или чужое
 *  значение (metrika-landing, R-4). */
export const parsePaymentId = (value: unknown): number | null =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : null;

/* Параметры landingOpened (metrika-landing, R-3). */
export const getLandingParams = (organization: IOrganization, source: EFunnelSource, entryType: EEntryType): ILandingParams => ({
  org_id: organization.id,
  entry_source: source,
  flow_type: getFlowType(organization),
  entry_type: entryType,
});

const isFresh = (moment: number, now: number, ttl: number): boolean => now - moment >= 0 && now - moment <= ttl;

const parseSnapshot = (raw: string | null): ICheckoutSnapshot | null => {
  if (!raw) {
    return null;
  }

  const value: Partial<ICheckoutSnapshot> | null = JSON.parse(raw);

  if (
    !value ||
    typeof value.uuid !== 'string' ||
    typeof value.started_at !== 'number' ||
    typeof value.org_id !== 'number' ||
    !PAYMENT_METHODS.includes(value.payment_method as string) ||
    !FLOW_TYPES.includes(value.flow_type as string)
  ) {
    return null;
  }

  const paymentId = parsePaymentId(value.payment_id);

  return {
    ...(paymentId !== null ? { payment_id: paymentId } : {}),
    uuid: value.uuid,
    org_id: value.org_id,
    source: parseFunnelSource(value.source ?? null),
    flow_type: value.flow_type as EFlowType,
    checkout_version: CHECKOUT_VERSION,
    payment_method: value.payment_method as EPaymentMethod,
    amount_source: value.amount_source === 'preset' ? 'preset' : 'custom',
    currency: value.currency === 'EUR' ? 'EUR' : 'RSD',
    guest_covers_commission: value.guest_covers_commission === true,
    started_at: value.started_at,
  };
};

/* Действующий снимок: есть и не старше 60 минут; иначе null. */
const readSnapshot = (now: number): ICheckoutSnapshot | null => {
  const snapshot = parseSnapshot(localStorage.getItem(EStorageKey.MetrikaCheckout));

  return snapshot && isFresh(snapshot.started_at, now, CHECKOUT_SNAPSHOT_TTL_MS) ? snapshot : null;
};

/* Действующий снимок официанта; сбой хранилища - как будто снимка нет (R-7). */
const readWaiterSnapshot = (waiterUuid: string, now: number): ICheckoutSnapshot | null => {
  try {
    const snapshot = readSnapshot(now);

    return snapshot?.uuid === waiterUuid ? snapshot : null;
  } catch {
    return null;
  }
};

const readCompletedMarks = (now: number): ICompletedMark[] => {
  const raw = localStorage.getItem(EStorageKey.MetrikaCompleted);
  const value: unknown = raw ? JSON.parse(raw) : [];

  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (mark): mark is ICompletedMark =>
      !!mark &&
      typeof mark.tuid === 'string' &&
      typeof mark.sent_at === 'number' &&
      isFresh(mark.sent_at, now, COMPLETED_MARK_TTL_MS),
  );
};

/* Снимок пишется в момент paymentStarted поверх прежнего: последняя начатая попытка (R-4). */
export const saveCheckoutSnapshot = (snapshot: ICheckoutSnapshot): void => {
  try {
    localStorage.setItem(EStorageKey.MetrikaCheckout, JSON.stringify(snapshot));
  } catch {
    // без снимка не будет только завершения и продолжения
  }
};

/** Параметры адреса экрана воронки, от которых зависят источник и вход. */
export interface IFunnelAddress {
  utmSource: string | null;
  source: string | null;
  /** uniqid адреса официанта; у адреса заведения null. */
  waiterUuid: string | null;
}

export interface IFunnelEntry {
  source: EFunnelSource;
  /** Адрес носителя или возврата бэка без продолжения: экран даёт landingOpened (R-3). */
  isEntry: boolean;
}

/* Источник и вход по адресу (metrika-landing, R-1..R-3). source ставит только сама страница: адрес с ним -
 *  внутренний, не вход; источник из utm_source, если метка есть, иначе из source. Адрес без source - вход,
 *  кроме продолжения: страница официанта при снимке того же официанта не старше 60 минут (возврат после
 *  отмены, «Попробовать снова», «назад» или перезагрузка адреса с носителя) - источник из метки, иначе из
 *  снимка. Новый вход - источник из метки или unknown. */
export const resolveFunnelEntry = ({ utmSource, source, waiterUuid }: IFunnelAddress, now: number): IFunnelEntry => {
  if (source !== null) {
    return { source: parseFunnelSource(utmSource ?? source), isEntry: false };
  }

  const continuation = waiterUuid ? readWaiterSnapshot(waiterUuid, now) : null;

  if (continuation) {
    return { source: utmSource !== null ? parseFunnelSource(utmSource) : continuation.source, isEntry: false };
  }

  return { source: parseFunnelSource(utmSource), isEntry: true };
};

/* Экран «спасибо»: снимок того же платежа (payment_id) не старше 60 минут, tuid не отмечен - в одном
 *  синхронном шаге снимок удаляется, tuid отмечается (старше 30 дней отбрасываются), снимок возвращается
 *  для события. Иначе null и хранилище не трогается (metrika-landing, R-4). */
export const takeCheckoutForCompletion = (tuid: string, paymentId: number, now: number): ICheckoutSnapshot | null => {
  try {
    const snapshot = readSnapshot(now);

    if (!snapshot || snapshot.payment_id !== paymentId) {
      return null;
    }

    const marks = readCompletedMarks(now);

    if (marks.some((mark) => mark.tuid === tuid)) {
      return null;
    }

    localStorage.removeItem(EStorageKey.MetrikaCheckout);
    localStorage.setItem(EStorageKey.MetrikaCompleted, JSON.stringify([...marks, { tuid, sent_at: now }]));

    return snapshot;
  } catch {
    return null;
  }
};
